import React, { useState, useCallback } from 'react';
import { zohoAPI } from '../lib/api';
import { formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle, Search, Loader2, Shield, Wrench, Database } from 'lucide-react';

export default function ReconcilePage() {
  const [loading, setLoading] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [debugSku, setDebugSku] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [fixing, setFixing] = useState(false);

  const runReconcile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await zohoAPI.reconcile(100);
      setReconcileData(r.data);
      if (r.data.mismatches_found === 0) toast.success('All items match Zoho!');
      else toast.warning(`${r.data.mismatches_found} mismatches found`);
    } catch (e) { toast.error('Failed to run reconciliation'); }
    finally { setLoading(false); }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try { const r = await zohoAPI.stockSyncStatus(); setSyncStatus(r.data); }
    catch (e) {}
  }, []);

  const handleFixAll = async () => {
    setFixing(true);
    try {
      const r = await zohoAPI.fixAllMismatches();
      toast.success(`Re-sync initiated: ${r.data.items_reset} items flagged for re-sync`);
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to initiate fix'); }
    finally { setFixing(false); }
  };

  const handleForceResync = async () => {
    setFixing(true);
    try {
      const r = await zohoAPI.recomputeStock();
      toast.success(`Stock re-sync started. ${r.data.first_batch?.items_processed || 0} items processed, ${r.data.first_batch?.remaining || '?'} remaining.`);
      fetchSyncStatus();
    } catch (e) { toast.error('Failed to start re-sync'); }
    finally { setFixing(false); }
  };

  const handleDebug = async () => {
    if (!debugSku.trim()) return;
    setLoading(true);
    try {
      const r = await zohoAPI.debugItem(debugSku.trim());
      setDebugData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to debug item');
      setDebugData(null);
    }
    finally { setLoading(false); }
  };

  React.useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  return (
    <div className="space-y-6" data-testid="reconcile-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="h-6 w-6 text-primary" />Reconcile with Zoho</h1>
        <p className="text-muted-foreground">Compare TradeIQ stock values with Zoho Inventory and fix mismatches</p>
      </div>

      {/* Sync Status + Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-sm"><CardContent className="p-4">
          <div className="text-xs font-medium text-muted-foreground uppercase mb-1">Stock Sync Progress</div>
          {syncStatus ? (
            <div>
              <p className="text-2xl font-bold font-mono">{syncStatus.percent_complete}%</p>
              <p className="text-xs text-muted-foreground">{formatNumber(syncStatus.stock_synced)} / {formatNumber(syncStatus.total_items)} items synced</p>
              {syncStatus.remaining > 0 && <p className="text-xs text-amber-600">{formatNumber(syncStatus.remaining)} remaining</p>}
            </div>
          ) : <Loader2 className="h-5 w-5 animate-spin" />}
        </CardContent></Card>

        <Card className="rounded-sm"><CardContent className="p-4 flex flex-col gap-2">
          <Button onClick={runReconcile} disabled={loading} className="rounded-sm w-full" data-testid="run-reconcile-btn">
            <Search className="h-4 w-4 mr-2" />{loading ? 'Running...' : 'Run Reconciliation (100 items)'}
          </Button>
          <Button variant="outline" onClick={handleForceResync} disabled={fixing} className="rounded-sm w-full" data-testid="force-resync-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${fixing ? 'animate-spin' : ''}`} />Force Full Re-Sync
          </Button>
        </CardContent></Card>

        <Card className="rounded-sm"><CardContent className="p-4 flex flex-col gap-2">
          <Button variant="destructive" onClick={handleFixAll} disabled={fixing} className="rounded-sm w-full" data-testid="fix-all-btn">
            <Wrench className="h-4 w-4 mr-2" />Fix All (Reset & Re-Sync)
          </Button>
          <Button variant="outline" onClick={fetchSyncStatus} className="rounded-sm w-full">
            <Database className="h-4 w-4 mr-2" />Refresh Status
          </Button>
        </CardContent></Card>
      </div>

      {/* Debug Single SKU */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Debug Single SKU</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input placeholder="Enter SKU (e.g., VTP08G4S2666)" value={debugSku} onChange={(e) => setDebugSku(e.target.value)} className="rounded-sm max-w-sm" data-testid="debug-sku-input" onKeyDown={(e) => e.key === 'Enter' && handleDebug()} />
            <Button onClick={handleDebug} disabled={loading || !debugSku.trim()} className="rounded-sm" data-testid="debug-btn">Debug</Button>
          </div>
          {debugData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">{debugData.sku}</Badge>
                <span className="text-xs text-muted-foreground">ID: {debugData.zoho_item_id}</span>
                {debugData.match === true && <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="h-3 w-3 mr-1" />Match</Badge>}
                {debugData.match === false && <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />Mismatch</Badge>}
              </div>
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead className="text-xs font-medium">Field</TableHead>
                  <TableHead className="text-xs font-medium text-right">Zoho Value</TableHead>
                  <TableHead className="text-xs font-medium text-right">TradeIQ Value</TableHead>
                  <TableHead className="text-xs font-medium text-right">Diff</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {["stock_on_hand", "committed_stock", "available_stock"].map(field => {
                    const zoho = debugData.zoho_values?.[field] ?? '-';
                    const tiq = debugData.tradeiq_values?.[field] ?? '-';
                    const diff = debugData.mismatches?.[field]?.diff;
                    const hasMismatch = diff !== undefined;
                    return (
                      <TableRow key={field} className={hasMismatch ? 'bg-red-50' : ''}>
                        <TableCell className="text-sm font-medium">{field}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{typeof zoho === 'number' ? formatNumber(zoho) : zoho}</TableCell>
                        <TableCell className={`text-right font-mono text-sm ${hasMismatch ? 'text-red-600 font-bold' : ''}`}>{typeof tiq === 'number' ? formatNumber(tiq) : tiq}</TableCell>
                        <TableCell className={`text-right font-mono text-sm ${hasMismatch ? 'text-red-600 font-bold' : 'text-emerald-600'}`}>{diff !== undefined ? (diff > 0 ? '+' : '') + formatNumber(diff) : '0'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {debugData.zoho_error && <p className="text-sm text-red-600">Zoho API error: {debugData.zoho_error}</p>}
              {debugData.zoho_warehouses?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Zoho Warehouses:</p>
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50">
                      <TableHead className="text-xs">Warehouse</TableHead>
                      <TableHead className="text-xs text-right">SOH</TableHead>
                      <TableHead className="text-xs text-right">Available</TableHead>
                      <TableHead className="text-xs text-right">Committed</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {debugData.zoho_warehouses.map((wh, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{wh.warehouse_name}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatNumber(wh.stock_on_hand)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatNumber(wh.available_stock)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatNumber(wh.committed_stock)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation Results */}
      {reconcileData && (
        <Card className="rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Reconciliation Results
              {reconcileData.mismatches_found === 0
                ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="h-3 w-3 mr-1" />All Match</Badge>
                : <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />{reconcileData.mismatches_found} Mismatches</Badge>
              }
              <span className="text-xs text-muted-foreground font-normal ml-2">Checked {reconcileData.items_checked} items</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reconcileData.mismatches?.length > 0 ? (
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead className="text-xs font-medium">SKU</TableHead>
                  <TableHead className="text-xs font-medium text-right">Zoho SOH</TableHead>
                  <TableHead className="text-xs font-medium text-right">TIQ SOH</TableHead>
                  <TableHead className="text-xs font-medium text-right">Zoho Committed</TableHead>
                  <TableHead className="text-xs font-medium text-right">TIQ Committed</TableHead>
                  <TableHead className="text-xs font-medium text-right">Zoho Available</TableHead>
                  <TableHead className="text-xs font-medium text-right">TIQ Available</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reconcileData.mismatches.map((m, i) => (
                    <TableRow key={i} className="bg-red-50/50">
                      <TableCell className="font-mono text-sm font-medium cursor-pointer text-primary hover:underline" onClick={() => { setDebugSku(m.sku); }}>{m.sku}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatNumber(m.zoho_stock_on_hand)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${m.zoho_stock_on_hand !== m.tradeiq_stock_on_hand ? 'text-red-600 font-bold' : ''}`}>{formatNumber(m.tradeiq_stock_on_hand)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatNumber(m.zoho_committed)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${m.zoho_committed !== m.tradeiq_committed ? 'text-red-600 font-bold' : ''}`}>{formatNumber(m.tradeiq_committed)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatNumber(m.zoho_available)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${m.zoho_available !== m.tradeiq_available ? 'text-red-600 font-bold' : ''}`}>{formatNumber(m.tradeiq_available)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
                <p className="text-lg font-medium text-emerald-700">All items match Zoho Inventory</p>
                <p className="text-sm text-muted-foreground">No mismatches detected in {reconcileData.items_checked} checked items</p>
              </div>
            )}
            {reconcileData.rate_limited && <p className="text-sm text-amber-600 mt-2">Rate limited - only partial check completed</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
